import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface MonthlyPracticeSectionProps {
  activeDays: string[];
}

export default function MonthlyPracticeSection({ activeDays }: MonthlyPracticeSectionProps) {
  const [isOpen, setIsOpen] = useState(false);

  const formatLocalDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();

  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();

  const firstDayOfWeek = firstDayOfMonth.getDay();
  const offsetDays = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;

  const activeDaysSet = new Set(activeDays.map(d => d.split('T')[0]));

  const calendarDays: (Date | null)[] = [];

  for (let i = 0; i < offsetDays; i++) {
    calendarDays.push(null);
  }

  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(new Date(year, month, day));
  }

  const todayStr = formatLocalDate(today);
  const monthName = today.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const daysActiveThisMonth = activeDays.filter(dateStr => {
    const d = new Date(dateStr);
    return d.getMonth() === month && d.getFullYear() === year;
  }).length;

  return (
    <div className="bg-[#181818] rounded-2xl border border-[#282828] overflow-hidden" style={{ boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)' }}>
      <div className={`${isOpen ? 'p-6' : 'p-6 pb-4'}`}>
        <h3 className="text-[14px] font-semibold text-[#F5F5F5] mb-1">
          Your Civic Practice (This Month)
        </h3>
        <p className="text-[11px] text-[#808080] mb-4 leading-relaxed">
          Each filled square is a day you showed up.
        </p>

        {isOpen && (
          <>
            <div className="text-[12px] text-[#A0A0A0] font-medium mb-3 text-center">
              {monthName}
            </div>

            <div className="grid grid-cols-7 gap-2 mb-2">
              {dayLabels.map(label => (
                <div key={label} className="text-[10px] text-[#606060] text-center font-medium">
                  {label}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-2 mb-4">
              {calendarDays.map((day, index) => {
                if (!day) {
                  return <div key={`empty-${index}`} className="aspect-square" />;
                }

                const dateStr = formatLocalDate(day);
                const isActive = activeDaysSet.has(dateStr);
                const isToday = dateStr === todayStr;

                return (
                  <div
                    key={dateStr}
                    className={`aspect-square rounded-lg transition-all flex items-center justify-center ${
                      isActive
                        ? 'bg-[#40E0D0]'
                        : 'bg-transparent border border-[#282828]'
                    } ${isToday ? 'ring-1 ring-[#40E0D0]/50' : ''}`}
                    title={day.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                  >
                    <span className={`text-[11px] font-medium ${
                      isActive ? 'text-[#111111]' : 'text-[#606060]'
                    }`}>
                      {day.getDate()}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {!isOpen && (
          <div className="text-[12px] text-[#A0A0A0] mb-3">
            {daysActiveThisMonth === 0 && "You haven't shown up yet this month."}
            {daysActiveThisMonth >= 1 && daysActiveThisMonth <= 4 && "You've shown up a few times this month."}
            {daysActiveThisMonth >= 5 && daysActiveThisMonth <= 14 && "You've been returning to civicbeat this month."}
            {daysActiveThisMonth >= 15 && "You've shown up consistently this month."}
          </div>
        )}
      </div>

      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-center gap-2 py-3 border-t border-[#282828] bg-[#181818] hover:bg-[#1A1A1A] transition-colors"
      >
        <span className="text-[12px] font-medium text-[#40E0D0]">
          {isOpen ? 'Hide calendar' : 'Show calendar'}
        </span>
        {isOpen ? (
          <ChevronUp className="w-4 h-4 text-[#40E0D0]" />
        ) : (
          <ChevronDown className="w-4 h-4 text-[#40E0D0]" />
        )}
      </button>
    </div>
  );
}
